import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

interface HeroCountdownProps {
    targetIso: string;
    label?: string;
    compact?: boolean;
    textColor?: string;
    labelFontSizePx?: number;
    valueFontSizePx?: number;
}

interface CountdownParts {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    finished: boolean;
}

function parseCountdown(targetMs: number, nowMs: number): CountdownParts {
    const diff = targetMs - nowMs;
    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, finished: true };
    }
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, finished: false };
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

const HeroCountdown: React.FC<HeroCountdownProps> = ({
    targetIso,
    label,
    compact = false,
    textColor = '#8a6d1f',
    labelFontSizePx = 10,
    valueFontSizePx = 20,
}) => {
    const { t } = useLanguage();
    const targetMs = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
    const valid = Number.isFinite(targetMs);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!valid) return undefined;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [valid]);

    if (!valid) return null;

    const parts = parseCountdown(targetMs, now);
    const displayLabel = label?.trim() || t('home.countdownLabel');
    const unitSizePx = Math.max(7, Math.round(labelFontSizePx * 0.9));

    const styleVars = {
        '--countdown-color': textColor,
        '--countdown-label-size': `${labelFontSizePx}px`,
        '--countdown-value-size': `${valueFontSizePx}px`,
        '--countdown-unit-size': `${unitSizePx}px`,
    } as React.CSSProperties;

    return (
        <div
            className={`hero-countdown${compact ? ' hero-countdown--compact' : ''}`}
            style={styleVars}
            role="timer"
            aria-live="polite"
        >
            <span className="hero-countdown-label">{displayLabel}</span>
            {parts.finished ? (
                <p className="hero-countdown-live">{t('home.countdownLive')}</p>
            ) : (
                <div className="hero-countdown-grid">
                    {[
                        { value: parts.days, unit: t('home.countdownDays') },
                        { value: parts.hours, unit: t('home.countdownHours') },
                        { value: parts.minutes, unit: t('home.countdownMinutes') },
                        { value: parts.seconds, unit: t('home.countdownSeconds') },
                    ].map((item) => (
                        <div key={item.unit} className="hero-countdown-item">
                            <span className="hero-countdown-value">{pad(item.value)}</span>
                            <span className="hero-countdown-unit">{item.unit}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HeroCountdown;
